import { useCallback, useMemo, useRef, useState } from 'react';
import { ConverterRegistry } from '../../core/converter';
import { ERROR_CODE_LABELS, errorSuggestion } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { detectFile } from '../../core/file-detect';
import { sha256Blob } from '../../core/hash';
import type {
  ConversionResult,
  ConvertOptions,
  ConverterCapabilities,
  ConverterMode,
  EngineErrorCode,
  FileMetadata,
  HashInfo,
} from '../../core/types';
import { createRegistry } from '../../engines';
import {
  finishConversion,
  newIdempotencyKey,
  startConversion,
  type FinishInput,
  type StartResult,
} from '../../engines/ai/client';
import type { HistoryRepository } from '../../history/repository';
import { getConversionDriver } from './worker-driver';

/** 超过该体积仅给出告警：本地转换仍可尝试，但内存风险明显上升。 */
export const LARGE_FILE_WARNING_BYTES = 128 * 1024 * 1024;

export type WorkbenchStatus = 'empty' | 'ready' | 'converting' | 'done' | 'error';

export interface WorkbenchError {
  code: EngineErrorCode | 'DETECTION' | 'NO_ENGINE' | 'CONSENT_REQUIRED';
  message: string;
  suggestion?: string;
}

export interface WorkbenchState {
  file: File | null;
  metadata: FileMetadata | null;
  hash: HashInfo | null;
  thumbnailUrl: string | null;
  targetFormat: string;
  /** 通道：本地解析或 AI 识别（同一目标格式可能两条通道都有）。 */
  channel: ConverterMode;
  status: WorkbenchStatus;
  progress: number;
  stage: string;
  result: ConversionResult | null;
  error: WorkbenchError | null;
  durationMs: number;
  /** 本次实际扣费（分，服务端返回值）；未发起时为 null。 */
  cost: number | null;
}

export interface NotifyInput {
  tone: 'info' | 'success' | 'warn' | 'error';
  title: string;
  description?: string;
}

/** 记账客户端（测试可注入假实现）。 */
export interface BillingClient {
  startConversion: typeof startConversion;
  finishConversion: typeof finishConversion;
}

export interface UseConversionsOptions {
  registry?: ConverterRegistry;
  repository: HistoryRepository;
  notify: (input: NotifyInput) => void;
  createThumbnail?: (file: File) => Promise<string | undefined>;
  billing?: BillingClient;
  /** 扣费/退款后回调：用于刷新「余额与免费次数」展示。 */
  onUsageChange?: () => void;
}

const INITIAL: WorkbenchState = {
  file: null,
  metadata: null,
  hash: null,
  thumbnailUrl: null,
  targetFormat: '',
  channel: 'local',
  status: 'empty',
  progress: 0,
  stage: '',
  result: null,
  error: null,
  durationMs: 0,
  cost: null,
};

const DEFAULT_BILLING: BillingClient = { startConversion, finishConversion };

export function useConversions(options: UseConversionsOptions) {
  const registry = useMemo(() => options.registry ?? createRegistry(), [options.registry]);
  const billing = options.billing ?? DEFAULT_BILLING;
  const [state, setState] = useState<WorkbenchState>(INITIAL);
  const [capabilities, setCapabilities] = useState<ConverterCapabilities[]>(() => registry.list());
  const [quality, setQuality] = useState(0.92);
  /** AI 通道的隐私确认：未勾选不允许上传。 */
  const [aiConsent, setAiConsent] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const refreshCapabilities = useCallback(async () => {
    const driver = getConversionDriver();
    if (!driver) return;
    try {
      const probed = await driver.requestCapabilities();
      if (probed.length > 0) setCapabilities(probed);
    } catch {
      // 能力探测失败时保留注册表声明，不阻塞使用。
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setState(INITIAL);
  }, []);

  const selectFile = useCallback(
    async (file: File) => {
      reset();
      const metadata = await detectFile(file);
      if (metadata.detection?.rejected) {
        const message = metadata.detection.rejectReason ?? '文件内容与扩展名明显不符，已拒绝处理。';
        options.notify({ tone: 'error', title: '无法处理该文件', description: message });
        return;
      }
      if (file.size > LARGE_FILE_WARNING_BYTES) {
        options.notify({
          tone: 'warn',
          title: '文件较大',
          description: '超过 128MB 的本地转换可能受浏览器内存限制，建议先在桌面端浏览器尝试。',
        });
      }
      const warnings = metadata.detection?.warnings ?? [];
      if (warnings.length > 0) options.notify({ tone: 'warn', title: '格式判定提示', description: warnings[0] });

      const targets = registry.targetsFor(metadata.type.extension);
      const hash = await sha256Blob(file);
      const thumbnailUrl = options.createThumbnail ? await options.createThumbnail(file) : undefined;
      const targetFormat = targets[0] ?? '';

      // 默认通道：同一目标格式若有本地通道就走本地（不上传、不涉及 AI 费用）。
      const candidates = targetFormat ? registry.candidatesFor(metadata.type.extension, targetFormat) : [];
      const channel: ConverterMode = candidates.some((item) => item.mode === 'local') ? 'local' : 'ai';

      setState({
        ...INITIAL,
        file,
        metadata,
        hash,
        thumbnailUrl: thumbnailUrl ?? null,
        targetFormat,
        channel,
        status: 'ready',
      });
    },
    [options, registry, reset],
  );

  const targets = useMemo(() => {
    if (!state.metadata) return [];
    return registry.targetsFor(canonicalExtension(state.metadata.type.extension));
  }, [registry, state.metadata]);

  /** 当前「源格式 → 目标格式」的全部通道（本地 / AI），UI 据此让用户选择。 */
  const candidates = useMemo(() => {
    if (!state.metadata || !state.targetFormat) return [];
    return registry.candidatesFor(state.metadata.type.extension, state.targetFormat);
  }, [registry, state.metadata, state.targetFormat]);

  /** 每个目标格式的通道与单价提示：格式下拉里逐项展示，数据仍来自能力注册表。 */
  const targetHints = useMemo(() => {
    const hints: Record<string, { modes: ConverterMode[]; estimatedCost: number }> = {};
    if (!state.metadata) return hints;
    for (const target of targets) {
      const list = registry.candidatesFor(state.metadata.type.extension, target);
      if (list.length === 0) continue;
      hints[target] = {
        modes: [...new Set(list.map((item) => item.mode))],
        estimatedCost: Math.min(...list.map((item) => item.estimatedCost)),
      };
    }
    return hints;
  }, [registry, state.metadata, targets]);

  const setTargetFormat = useCallback(
    (targetFormat: string) => {
      setState((current) => {
        if (!current.metadata) return { ...current, targetFormat, result: null, error: null, status: 'empty' };
        const next = registry.candidatesFor(current.metadata.type.extension, targetFormat);
        const channel: ConverterMode = next.some((item) => item.mode === current.channel)
          ? current.channel
          : next.some((item) => item.mode === 'local')
            ? 'local'
            : 'ai';
        return { ...current, targetFormat, channel, result: null, error: null, status: 'ready' };
      });
    },
    [registry],
  );

  const setChannel = useCallback((channel: ConverterMode) => {
    setState((current) => ({ ...current, channel, result: null, error: null, status: current.file ? 'ready' : 'empty' }));
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** 结算：成功落状态、失败退款；结算本身失败时提示「稍后自动退款」，不阻塞用户。 */
  const settle = useCallback(
    async (jobId: string, input: FinishInput) => {
      try {
        const outcome = await billing.finishConversion(jobId, input);
        if (outcome.refunded) {
          options.notify({
            tone: 'info',
            title: '费用已退还',
            description: '本次转换未完成，扣掉的积分/次数已自动退还。',
          });
        }
      } catch {
        options.notify({
          tone: 'warn',
          title: '结算稍后自动完成',
          description: '网络异常导致结算未送达；系统会在你下次发起转换时自动核对并退款。',
        });
      } finally {
        options.onUsageChange?.();
      }
    },
    [billing, options],
  );

  const start = useCallback(async () => {
    const { file, metadata, hash, targetFormat, thumbnailUrl, channel } = state;
    if (!file || !metadata || !targetFormat) return;

    const candidatesForStart = registry.candidatesFor(metadata.type.extension, targetFormat);
    const capability = candidatesForStart.find((item) => item.mode === channel) ?? candidatesForStart[0];
    if (!capability) {
      const error: WorkbenchError = {
        code: 'NO_ENGINE',
        message: `当前版本不支持 ${metadata.type.extension} → ${targetFormat}`,
      };
      setState((current) => ({ ...current, status: 'error', error }));
      options.notify({ tone: 'error', title: '不支持该转换组合', description: error.message });
      return;
    }

    if (capability.requiresUpload && !aiConsent) {
      const error: WorkbenchError = {
        code: 'CONSENT_REQUIRED',
        message: 'AI 识别会把文件内容上传到服务器与第三方模型，请先勾选确认。',
      };
      setState((current) => ({ ...current, error }));
      options.notify({ tone: 'warn', title: '请先确认隐私说明', description: error.message });
      return;
    }

    const mainThread = capability.runtime === 'main-thread';
    const driver = mainThread ? null : getConversionDriver();
    if (!mainThread && !driver) {
      options.notify({ tone: 'error', title: '当前环境不支持 Web Worker', description: '请在最新版桌面浏览器中打开本站。' });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setState((current) => ({
      ...current,
      status: 'converting',
      progress: 0,
      stage: capability.mode === 'ai' ? '记账中' : '准备',
      result: null,
      error: null,
      cost: null,
    }));

    // 1) 记账：本地只上报元数据；AI 会先扣 2 分。未登录走每日免费次数。
    let job: StartResult;
    try {
      job = await billing.startConversion({
        mode: capability.mode,
        sourceFormat: canonicalExtension(metadata.type.extension),
        targetFormat,
        sourceSize: file.size,
        engineId: capability.id,
        idempotencyKey: newIdempotencyKey(),
      });
    } catch (error) {
      abortRef.current = null;
      const code = engineErrorCode(error);
      const message = error instanceof Error ? error.message : String(error);
      setState((current) => ({
        ...current,
        status: 'error',
        progress: 0,
        stage: '',
        error: { code, message, suggestion: errorSuggestion(code) },
      }));
      options.notify({ tone: 'error', title: ERROR_CODE_LABELS[code] ?? '无法开始转换', description: message });
      return;
    }

    options.onUsageChange?.();
    setState((current) => ({ ...current, cost: job.cost, stage: '转换中' }));

    const startedAt = performance.now();
    const convertOptions: ConvertOptions = { targetFormat, quality, jobId: job.jobId };
    const onProgress = (progress: { progress: number; stage: string }) =>
      setState((current) =>
        current.status === 'converting' ? { ...current, progress: progress.progress, stage: progress.stage } : current,
      );

    try {
      let result: ConversionResult;
      if (mainThread) {
        const converter = registry.byId(capability.id);
        if (!converter) throw new Error(`引擎未注册：${capability.id}`);
        result = await converter.convert(file, convertOptions, { signal: controller.signal, onProgress });
      } else {
        const buffer = await file.arrayBuffer();
        result = await driver!.run(
          {
            converterId: capability.id,
            targetFormat,
            fileName: file.name,
            mime: file.type || 'application/octet-stream',
            buffer,
            options: convertOptions,
          },
          { signal: controller.signal, onProgress },
        );
      }

      const durationMs = Math.round(performance.now() - startedAt);
      const verified = result.verificationPassed;

      await settle(job.jobId, { status: 'succeeded', outputSize: result.blob.size });

      setState((current) => ({
        ...current,
        status: verified ? 'done' : 'error',
        progress: 100,
        stage: verified ? '完成' : '校验失败',
        result,
        durationMs,
        error: verified
          ? null
          : {
              code: 'VERIFICATION_FAILED',
              // 带上引擎给出的具体断言：只说「未通过校验」用户既没法自查也没法反馈
              // （只给一句兜底文案时，连「哪条判据挂了」都要靠翻代码猜）。
              message: result.verificationDetails?.length
                ? `产物未通过完整性校验，已阻止下载：${result.verificationDetails.join('；')}`
                : '产物未通过完整性校验，已阻止下载。',
              suggestion: errorSuggestion('VERIFICATION_FAILED'),
            },
      }));

      await options.repository.save({
        sourceName: file.name,
        sourceFormat: metadata.type.extension,
        targetFormat,
        sourceSize: file.size,
        outputSize: result.blob.size,
        engine: capability.id,
        mode: capability.mode,
        cost: job.cost,
        status: verified ? 'completed' : 'failed',
        fidelityLevel: result.fidelity.level,
        fidelitySummary: result.fidelity.summary,
        durationMs,
        hash: hash ?? { value: 'unknown', partial: false, bytesHashed: 0 },
        errorCode: verified ? undefined : 'VERIFICATION_FAILED',
        thumbnailDataUrl: thumbnailUrl ?? undefined,
      });

      if (verified) {
        options.notify({
          tone: 'success',
          title: '转换完成',
          description: `${result.fidelity.level} · ${result.fidelity.summary}`,
        });
      } else {
        options.notify({ tone: 'error', title: '产物校验未通过', description: '已阻止下载，可更换目标格式后重试。' });
      }
    } catch (error) {
      const durationMs = Math.round(performance.now() - startedAt);
      const code = engineErrorCode(error);
      const message = error instanceof Error ? error.message : String(error);
      const cancelled = code === 'CANCELLED';

      // 失败/取消都结算：服务端按 job 退款（同一 job 重复结算幂等）。
      await settle(job.jobId, { status: 'failed', errorCode: code });

      if (cancelled) {
        setState((current) => ({ ...current, status: 'ready', progress: 0, stage: '', error: null }));
        options.notify({ tone: 'info', title: '已取消转换' });
      } else {
        setState((current) => ({ ...current, status: 'error', error: { code, message, suggestion: errorSuggestion(code) } }));
        options.notify({ tone: 'error', title: ERROR_CODE_LABELS[code] ?? '转换失败', description: message });
      }

      await options.repository.save({
        sourceName: file.name,
        sourceFormat: metadata.type.extension,
        targetFormat,
        sourceSize: file.size,
        outputSize: 0,
        engine: capability.id,
        mode: capability.mode,
        cost: job.cost,
        status: cancelled ? 'cancelled' : 'failed',
        fidelityLevel: 'L3',
        fidelitySummary: '未完成',
        durationMs,
        hash: hash ?? { value: 'unknown', partial: false, bytesHashed: 0 },
        errorCode: code,
        thumbnailDataUrl: thumbnailUrl ?? undefined,
      });
    } finally {
      abortRef.current = null;
    }
  }, [aiConsent, billing, options, quality, registry, settle, state]);

  /** 仅在产物通过校验时允许下载；内存中的 blob 生命周期由 URL 管理。 */
  const download = useCallback(() => {
    const result = state.result;
    if (!result || !result.verificationPassed || !state.file) return;
    const url = URL.createObjectURL(result.blob);
    objectUrlRef.current = url;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = result.fileName || buildOutputName(state.file.name, state.targetFormat);
    anchor.rel = 'noopener';
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }, [state.file, state.result, state.targetFormat]);

  return {
    state,
    capabilities,
    targets,
    candidates,
    targetHints,
    quality,
    setQuality,
    aiConsent,
    setAiConsent,
    setChannel,
    selectFile,
    setTargetFormat,
    start,
    cancel,
    reset,
    download,
    refreshCapabilities,
  };
}

function engineErrorCode(error: unknown): EngineErrorCode {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code === 'string') return code as EngineErrorCode;
  return 'INTERNAL_ERROR';
}
